import { useEffect } from "react";
import { X, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { AddressAutocomplete, type AddressData } from "@/components/ui/AddressAutocomplete";

export interface ProjectFormData {
  zaaknummer: string;
  address: AddressData | null;
}

interface NewConversationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateProject: (data: ProjectFormData) => Promise<void>;
  onRefreshProjects?: () => void;
}

const SESSION_STORAGE_KEY = "newProjectDraft";

const projectSchema = z.object({
  zaaknummer: z.string().min(1, "Zaaknummer is required"),
  addressInput: z.string(),
  address: z.custom<AddressData | null>((value) => value !== null, {
    message: "Address is required",
  }),
});

type ProjectFormFields = z.infer<typeof projectSchema>;

/**
 * Modal for creating a new project/conversation.
 * Collects zaaknummer (case number) and address via Google Places autocomplete.
 * Saves draft data to sessionStorage to prevent data loss.
 */
export function NewConversationModal({
  isOpen,
  onClose,
  onCreateProject,
  onRefreshProjects,
}: NewConversationModalProps) {
  const { t } = useTranslation("common");

  // Load initial values from sessionStorage
  const getInitialValues = (): ProjectFormFields => {
    const saved = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      return {
        zaaknummer: parsed.zaaknummer || "",
        addressInput: parsed.addressInput || "",
        address: parsed.address || null,
      };
    }
    return { zaaknummer: "", addressInput: "", address: null };
  };

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { isSubmitting },
    reset,
  } = useForm<ProjectFormFields>({
    resolver: zodResolver(projectSchema),
    mode: "onChange",
    defaultValues: getInitialValues(),
  });

  const { zaaknummer, addressInput, address } = watch();

  // Save to sessionStorage whenever form data changes
  useEffect(() => {
    const draftData = { zaaknummer, addressInput, address };
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(draftData));
  }, [zaaknummer, addressInput, address]);

  if (!isOpen) return null;

  const onSubmit = async (data: ProjectFormFields) => {
    const submitData: ProjectFormData = {
      zaaknummer: data.zaaknummer.trim(),
      address: data.address,
    };

    try {
      await onCreateProject(submitData);
      // Reset form and clear sessionStorage after success
      reset({ zaaknummer: "", addressInput: "", address: null });
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
    } catch (error) {
      console.error("Failed to create conversation:", error);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    // Don't clear form data or sessionStorage - keep the draft

    // Refresh projects list when going back
    if (onRefreshProjects) {
      onRefreshProjects();
    }

    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && zaaknummer.trim() && address) {
      handleSubmit(onSubmit)();
    } else if (e.key === "Escape") {
      handleClose();
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex flex-col bg-background md:items-center md:justify-center md:bg-utrecht-gray/90 md:backdrop-blur-sm">
      <div className="flex flex-1 flex-col bg-background md:flex-initial md:h-[90vh] md:max-h-[900px] md:w-full md:max-w-2xl md:overflow-hidden">
        {/* Header */}
        <div className="border-b border-border bg-utrecht-blue p-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-white">
                {t("infoModal.conversationName.title")}
              </h2>
              <p className="text-sm text-white/90 mt-1">
                {t("infoModal.conversationName.subtitle")}
              </p>
            </div>
            <button
              onClick={handleClose}
              disabled={isSubmitting}
              className="p-2 text-white hover:bg-white/10 transition-colors"
              aria-label="Sluiten"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Content - overflow-y-auto for scrolling when needed */}
        <form onSubmit={handleSubmit(onSubmit)} className="flex-1 flex flex-col p-4 pt-2 min-h-0 overflow-y-auto">
          {/* Zaaknummer field */}
          <div className="space-y-1 flex-shrink-0">
            <Label htmlFor="zaaknummer" className="text-base">
              {t("infoModal.conversationName.label")}
            </Label>
            <Input
              id="zaaknummer"
              type="text"
              placeholder={t("infoModal.conversationName.placeholder")}
              autoFocus
              disabled={isSubmitting}
              className="h-16 text-lg"
              onKeyDown={handleKeyDown}
              {...register("zaaknummer")}
            />
          </div>

          {/* Address field */}
          <div className="space-y-1 mt-2 flex-1 flex flex-col min-h-0">
            <Label htmlFor="address" className="text-base">
              {t("infoModal.address.label")}
            </Label>
            <AddressAutocomplete
              id="address"
              value={addressInput}
              onChange={(value) => setValue("addressInput", value)}
              onSelect={(addressData) => setValue("address", addressData)}
              placeholder={t("infoModal.address.placeholder")}
              disabled={isSubmitting}
              showMap={true}
              selectedAddress={address}
              className="flex-1 min-h-0"
            />
          </div>

          {/* Footer */}
          <div className="border-t border-border pt-4 flex flex-col gap-3 flex-shrink-0 mt-4">
            <Button
              type="submit"
              disabled={!zaaknummer.trim() || !address || isSubmitting}
              variant="brand"
              size="xxl"
              className="w-full"
            >
              {isSubmitting ? <Loader2 className="h-5 w-5 animate-spin" /> : t("infoModal.finishButton")}
            </Button>
            <Button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              variant="outline"
              size="xxl"
              className="w-full"
            >
              {t("infoModal.backButton")}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
